import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, RefreshCw, FileSpreadsheet, MapPin, Database, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { tallyApi, gstinApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast/Toast';

type Row = { pincode: string; city: string; state: string; our_count: number; not_our_count: number; total: number };
type Totals = { our_count: number; not_our_count: number; total: number; pincodes: number };
type GstinRow = { pincode: string; city: string; state: string; gstin_count: number };
type GstinTotals = { gstin_count: number; pincodes: number };
type Mode = 'tally' | 'gstin';
type SortKey = 'pincode' | 'city' | 'state' | 'our_count' | 'not_our_count' | 'total' | 'gstin_count';
type SortDir = 'asc' | 'desc';

const fmt = (n: any) => Number(n || 0).toLocaleString('en-IN');

export default function PincodeSummary() {
  const { showError, showSuccess } = useToast();
  const { canView } = useAuth();
  const navigate = useNavigate();
  const canGstin = canView('gstin');

  const [mode, setMode] = useState<Mode>('tally');
  // Drill into the OC / NOC Visit report (tally) or the GSTIN database report.
  const drillVisit = (type: 'our' | 'not-our', pincode: string) =>
    navigate(`/visit/${type === 'our' ? 'oc' : 'noc'}-report?pincode=${encodeURIComponent(pincode)}`);
  const drillGstin = (pincode: string) =>
    navigate(`/gstin?pincode=${encodeURIComponent(pincode)}`);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<Totals>({ our_count: 0, not_our_count: 0, total: 0, pincodes: 0 });
  const [gstinRows, setGstinRows] = useState<GstinRow[]>([]);
  const [gstinTotals, setGstinTotals] = useState<GstinTotals>({ gstin_count: 0, pincodes: 0 });

  const [sortKey, setSortKey] = useState<SortKey>('total');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Switching modes resets the sort to that mode's primary count column.
  const switchMode = (m: Mode) => {
    if (m === mode) return;
    setMode(m);
    setSortKey(m === 'gstin' ? 'gstin_count' : 'total');
    setSortDir('desc');
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'gstin') {
        const res = await gstinApi.getPincodeSummary({ search: debounced || undefined });
        setGstinRows(res.data?.rows || []);
        setGstinTotals(res.data?.totals || { gstin_count: 0, pincodes: 0 });
      } else {
        const res = await tallyApi.getPincodeSummary({ search: debounced || undefined });
        setRows(res.rows || []);
        setTotals(res.totals || { our_count: 0, not_our_count: 0, total: 0, pincodes: 0 });
      }
    } catch (e: any) {
      showError('Error', e?.message || 'Failed to load pincode summary');
    } finally {
      setLoading(false);
    }
  }, [mode, debounced, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const src: any[] = mode === 'gstin' ? gstinRows : rows;
    return [...src].sort((a, b) => {
      const av = (a as any)[sortKey], bv = (b as any)[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  }, [mode, rows, gstinRows, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'pincode' || k === 'city' || k === 'state' ? 'asc' : 'desc'); }
  };
  const SortIcon = ({ col }: { col: SortKey }) =>
    sortKey !== col ? <ChevronsUpDown size={11} className="inline ml-1 text-slate-400" />
      : sortDir === 'asc' ? <ChevronUp size={11} className="inline ml-1 text-blue-600" />
        : <ChevronDown size={11} className="inline ml-1 text-blue-600" />;

  const exportExcel = async () => {
    if (sorted.length === 0) { showError('Export', 'No data to export'); return; }
    const { utils, writeFile } = await import('xlsx');
    let data: any[];
    if (mode === 'gstin') {
      data = (sorted as GstinRow[]).map((r, i) => ({
        'Sr': i + 1, 'Pincode': r.pincode, 'City': r.city, 'State': r.state, 'GSTIN Count': r.gstin_count,
      }));
      data.push({ 'Sr': '', 'Pincode': 'TOTAL', 'City': '', 'State': '', 'GSTIN Count': gstinTotals.gstin_count });
    } else {
      data = (sorted as Row[]).map((r, i) => ({
        'Sr': i + 1, 'Pincode': r.pincode, 'City': r.city, 'State': r.state,
        'Our': r.our_count, 'Not Our': r.not_our_count, 'Total': r.total,
      }));
      data.push({ 'Sr': '', 'Pincode': 'TOTAL', 'City': '', 'State': '',
        'Our': totals.our_count, 'Not Our': totals.not_our_count, 'Total': totals.total });
    }
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, mode === 'gstin' ? 'GSTIN Pincode' : 'Pincode Summary');
    const tag = mode === 'gstin' ? 'GSTIN' : 'Our_NotOur';
    writeFile(wb, `Pincode_${tag}_${new Date().toISOString().split('T')[0]}.xlsx`);
    showSuccess('Exported', 'Excel downloaded');
  };

  const th = 'px-3 py-2 text-[12px] font-bold text-gray-700 border border-gray-300 bg-slate-100 select-none cursor-pointer hover:text-blue-700';
  const td = 'px-3 py-1.5 text-[13px] text-gray-700 border border-gray-200';
  const colCount = mode === 'gstin' ? 5 : 7;

  return (
    <div className="p-3 sm:p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-bold text-gray-800">Pincode-wise Customer Count</h1>
          <span className="text-[12px] text-gray-400 italic hidden sm:inline">
            {mode === 'gstin' ? 'GSTIN Database' : 'Our vs Not Our (Tally)'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Mode toggle — GSTIN option only when the user can view the DB. */}
          <div className="inline-flex rounded border border-slate-300 overflow-hidden">
            <button onClick={() => switchMode('tally')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium transition-colors ${mode === 'tally' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-slate-50'}`}>
              <MapPin size={14} /> Tally
            </button>
            {canGstin && (
              <button onClick={() => switchMode('gstin')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium border-l border-slate-300 transition-colors ${mode === 'gstin' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-slate-50'}`}>
                <Database size={14} /> GSTIN Database
              </button>
            )}
          </div>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search pincode / city / state…"
              className="pl-8 pr-8 py-1.5 border border-slate-300 rounded text-[13px] w-64 outline-none focus:ring-1 focus:ring-blue-300 bg-white" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={13} /></button>
            )}
          </div>
          <button onClick={fetchData} className="p-2 rounded text-slate-500 hover:bg-slate-100" title="Refresh">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={exportExcel}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-300 rounded text-[13px] text-green-700 bg-green-50 hover:bg-green-100">
            <FileSpreadsheet size={14} /> Excel
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      {mode === 'gstin' ? (
        <div className="grid grid-cols-2 gap-2 mb-3 sm:max-w-md">
          <StatTile label="Pincodes" value={fmt(gstinTotals.pincodes)} color="text-slate-700" />
          <StatTile label="GSTIN Records" value={fmt(gstinTotals.gstin_count)} color="text-indigo-700" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <StatTile label="Pincodes" value={fmt(totals.pincodes)} color="text-slate-700" />
          <StatTile label="Our Customers" value={fmt(totals.our_count)} color="text-emerald-700" />
          <StatTile label="Not Our Customers" value={fmt(totals.not_our_count)} color="text-red-700" />
          <StatTile label="Total" value={fmt(totals.total)} color="text-blue-700" />
        </div>
      )}

      {/* Table */}
      <div className="overflow-auto border border-gray-300 rounded bg-white" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={`${th} w-[50px] text-center`}>#</th>
              <th className={`${th} text-left`} onClick={() => toggleSort('pincode')}>Pincode<SortIcon col="pincode" /></th>
              <th className={`${th} text-left`} onClick={() => toggleSort('city')}>City<SortIcon col="city" /></th>
              <th className={`${th} text-left`} onClick={() => toggleSort('state')}>State<SortIcon col="state" /></th>
              {mode === 'gstin' ? (
                <th className={`${th} text-right`} onClick={() => toggleSort('gstin_count')}>GSTIN Count<SortIcon col="gstin_count" /></th>
              ) : (
                <>
                  <th className={`${th} text-right`} onClick={() => toggleSort('our_count')}>Our<SortIcon col="our_count" /></th>
                  <th className={`${th} text-right`} onClick={() => toggleSort('not_our_count')}>Not Our<SortIcon col="not_our_count" /></th>
                  <th className={`${th} text-right`} onClick={() => toggleSort('total')}>Total<SortIcon col="total" /></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-slate-400 text-sm">No data found</td></tr>
            ) : mode === 'gstin' ? (sorted as GstinRow[]).map((r, i) => (
              <tr key={r.pincode + i} className={i % 2 ? 'bg-slate-50/60' : 'bg-white'}>
                <td className={`${td} text-center text-slate-400 tabular-nums`}>{i + 1}</td>
                <td className={`${td} font-semibold text-slate-800 tabular-nums`}>{r.pincode}</td>
                <td className={td}>{r.city || '—'}</td>
                <td className={td}>{r.state || '—'}</td>
                <td className={`${td} text-right tabular-nums`}>
                  {r.gstin_count > 0 ? (
                    <button onClick={() => drillGstin(r.pincode)}
                      className="text-indigo-700 font-semibold hover:underline decoration-dotted"
                      title={`View ${r.gstin_count} GSTIN records in ${r.pincode}`}>{fmt(r.gstin_count)}</button>
                  ) : <span className="text-slate-300">0</span>}
                </td>
              </tr>
            )) : (sorted as Row[]).map((r, i) => (
              <tr key={r.pincode + i} className={i % 2 ? 'bg-slate-50/60' : 'bg-white'}>
                <td className={`${td} text-center text-slate-400 tabular-nums`}>{i + 1}</td>
                <td className={`${td} font-semibold text-slate-800 tabular-nums`}>{r.pincode}</td>
                <td className={td}>{r.city || '—'}</td>
                <td className={td}>{r.state || '—'}</td>
                <td className={`${td} text-right tabular-nums`}>
                  {r.our_count > 0 ? (
                    <button onClick={() => drillVisit('our', r.pincode)}
                      className="text-emerald-700 font-medium hover:underline decoration-dotted"
                      title={`View ${r.our_count} Our customers in ${r.pincode}`}>{fmt(r.our_count)}</button>
                  ) : <span className="text-slate-300">0</span>}
                </td>
                <td className={`${td} text-right tabular-nums`}>
                  {r.not_our_count > 0 ? (
                    <button onClick={() => drillVisit('not-our', r.pincode)}
                      className="text-red-700 font-medium hover:underline decoration-dotted"
                      title={`View ${r.not_our_count} Not-Our customers in ${r.pincode}`}>{fmt(r.not_our_count)}</button>
                  ) : <span className="text-slate-300">0</span>}
                </td>
                <td className={`${td} text-right tabular-nums font-bold text-blue-700`}>{fmt(r.total)}</td>
              </tr>
            ))}
          </tbody>
          {sorted.length > 0 && (
            <tfoot>
              {mode === 'gstin' ? (
                <tr className="sticky bottom-0 bg-slate-200 font-bold">
                  <td className={`${td} bg-slate-200`} colSpan={4}>Total — {fmt(gstinTotals.pincodes)} pincodes</td>
                  <td className={`${td} bg-slate-200 text-right tabular-nums text-indigo-800`}>{fmt(gstinTotals.gstin_count)}</td>
                </tr>
              ) : (
                <tr className="sticky bottom-0 bg-slate-200 font-bold">
                  <td className={`${td} bg-slate-200`} colSpan={4}>Total — {fmt(totals.pincodes)} pincodes</td>
                  <td className={`${td} bg-slate-200 text-right tabular-nums text-emerald-800`}>{fmt(totals.our_count)}</td>
                  <td className={`${td} bg-slate-200 text-right tabular-nums text-red-800`}>{fmt(totals.not_our_count)}</td>
                  <td className={`${td} bg-slate-200 text-right tabular-nums text-blue-800`}>{fmt(totals.total)}</td>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function StatTile({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
